import once from 'lodash/once';

import { get } from '@lumapps/constants';
import { ToastType } from '@lumapps/constants/types';
import { homePageView } from '@lumapps/contents/routes/view';
import { UseNotification } from '@lumapps/notifications/hooks/useNotifications';
import { RouterApi } from '@lumapps/router/api';
import { rgbaToHex } from '@lumapps/utils/color';

import { getBeekeeperToken } from '../api';

/**
 * Initialize Beekeeper web component context
 */
export const initBeekeeperContext = once(
    async ({ notifications, slug, router }: { notifications: UseNotification; slug: string; router: RouterApi }) => {
        const { userLang, isProd, isStaging, bkprEnvironment } = get();

        let defaultWebUrl = '';
        let token: string;
        let expiresAt: string | undefined;
        try {
            const response = await getBeekeeperToken();
            defaultWebUrl = response.defaultWebUrl;
            token = response.token;
            expiresAt = response.expiresAt;
        } catch (e) {
            // TODO: remove once the token exchange finally works
            token = localStorage.getItem('BEEKEEPER_TOKEN') as string;
            if (!token) {
                throw e;
            }
            // No expiration for fallback tokens (same as API behavior)
            expiresAt = undefined;
        }

        const bodyStyles = window.getComputedStyle(document.body);
        window.BeekeeperContext = {
            environment: 'develop',
            token,
            expiresAt,
            pageClose() {
                router.redirect(homePageView({ isV2Compatible: true }));
            },
            async refreshToken() {
                const response = await getBeekeeperToken();
                const ctx = window.BeekeeperContext;
                if (ctx) {
                    ctx.token = response.token;
                    ctx.expiresAt = response.expiresAt;
                }
                return response.token;
            },
            site: slug,
            vanityUrl: window.location.origin,
            tenantUrl: defaultWebUrl as string,
            locale: userLang,
            // eslint-disable-next-line lumapps/no-ds-classes
            primaryColor: bodyStyles.getPropertyValue('--lumx-color-primary-N'),
            // eslint-disable-next-line lumapps/no-ds-classes
            fontFamily: bodyStyles.getPropertyValue('--lumx-typography-font-family'),
            showToast(text: string, type: ToastType) {
                switch (type) {
                    case ToastType.SUCCESS:
                        notifications.success({ translate: text });
                        break;
                    case ToastType.INFORMATION:
                        notifications.info({ translate: text });
                        break;
                    case ToastType.WARNING:
                        notifications.warning({ translate: text });
                        break;
                    case ToastType.ERROR:
                        notifications.error({ translate: text });
                        break;
                    default:
                        notifications.info({ translate: text });
                }
            },
        };

        /**
         * We need to make sure that the primary color is in hex format.
         */
        if (window.BeekeeperContext.primaryColor.startsWith('rgb')) {
            window.BeekeeperContext.primaryColor = rgbaToHex(window.BeekeeperContext.primaryColor).hex;
        }

        if (isProd) {
            window.BeekeeperContext.environment = 'production';
        } else if (isStaging) {
            window.BeekeeperContext.environment = 'staging';
        }

        if (bkprEnvironment === 'local') {
            window.BeekeeperContext.tenantUrl = window.BeekeeperCdn;
        }
    },
);
